import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { UI, DISPLAY } from '../styles/tl5';
import { ROUTES } from '../constants/routes';
import {
  useListBlogCategoriesQuery,
  useListBlogArticlesQuery,
} from '../store/siteContentApi';
import featuredImg from '../assets/img/v5/guide-lipid.jpg';

// Guides — the reference's "Health guides" index, replacing the old blog
// index at /blog. Topic chips, one featured card, then a card grid.
//
// Where ours differs from the reference, and why:
//
//   · Topics are the six real blog categories, not the reference's ten
//     invented ones. Their labels carry an emoji prefix that duplicates
//     the separate `icon` field, and the v5 chips are plain words, so the
//     emoji is stripped for display.
//   · The reference hardcodes one featured article. Our `featured` flag
//     marks one article per category, which is a better fit: the hero
//     follows the selected topic, and on "All" it is the first featured
//     article. Either way it is dropped from the grid below so it never
//     appears twice.
//   · No article has a cover image — the records carry a `gradient`, not
//     a photograph (API-NOTES 2.29). The reference already has a
//     no-image card: a coloured tile with the topic set in large display
//     type. Every grid card uses it. The per-article `gradient` from the
//     API is not used because those are the old design's category
//     colours (reds, purples, blues) and clash with the v5 palette; the
//     tiles use the reference's own four brand colours instead. The
//     reference cycles them by card index, which gives the same category
//     four different colours down one page — odd when the tile's text is
//     the category name. Here the colour is keyed to the category, so it
//     is stable wherever that topic appears.
//   · Cards still link to /blog/:slug, which opens the old article view.
//     Guide Article is the next page to build and will replace it.

const TILE_PALETTE = [
  ['var(--tl-yellow)', 'var(--tl-g9)'],
  ['var(--tl-teal)', '#FFFFFF'],
  ['var(--tl-g9)', 'var(--tl-teal-l)'],
  ['#D5EFE8', 'var(--tl-g9)'],
];

// Category labels arrive as "🩸 Diabetes" — the emoji repeats the `icon`
// field and the v5 chips are typographic.
const stripEmoji = (label) => String(label || '')
  .replace(/^[^\p{L}\p{N}]+/u, '')
  .trim();

const ALL = 'all';

const Guides = () => {
  const { data: categories = [], isLoading: catsLoading } = useListBlogCategoriesQuery();
  const { data: articles = [], isLoading: articlesLoading } = useListBlogArticlesQuery();
  const loading = catsLoading || articlesLoading;

  const [topic, setTopic] = useState(ALL);

  const labelFor = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[c.slug] = stripEmoji(c.label) || c.slug; });
    return map;
  }, [categories]);

  const sorted = useMemo(
    () => [...articles].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)),
    [articles],
  );

  const inTopic = useMemo(
    () => (topic === ALL ? sorted : sorted.filter((a) => a.category_slug === topic)),
    [sorted, topic],
  );

  // One featured per category, so the hero follows whichever topic is on.
  const featured = inTopic.find((a) => a.featured) || null;
  const rest = featured ? inTopic.filter((a) => a.slug !== featured.slug) : inTopic;

  const chips = [{ slug: ALL, label: 'All' }, ...categories.map((c) => ({ slug: c.slug, label: stripEmoji(c.label) || c.slug }))];

  // Tile colour follows the category, not the card's position.
  const tileFor = useMemo(() => {
    const map = {};
    categories.forEach((c, i) => { map[c.slug] = TILE_PALETTE[i % TILE_PALETTE.length]; });
    return map;
  }, [categories]);

  const href = (a) => `${ROUTES.BLOG}/${a.slug}`;

  return (
    <Box
      component="main"
      sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {/* ── topics ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 1, px: 0.5 }}>
        {loading && [0, 1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ width: 104, height: 40, borderRadius: '999px', bgcolor: '#FFFFFF' }} />
        ))}
        {!loading && chips.map((c) => {
          const on = c.slug === topic;
          return (
            <Box
              key={c.slug}
              component="button"
              type="button"
              onClick={() => setTopic(c.slug)}
              aria-pressed={on}
              sx={{
                border: 0,
                cursor: 'pointer',
                borderRadius: '999px',
                p: '10px 16px',
                whiteSpace: 'nowrap',
                fontFamily: UI,
                fontSize: 14,
                fontWeight: 700,
                bgcolor: on ? 'var(--tl-g9)' : '#FFFFFF',
                color: on ? '#FFFFFF' : 'var(--tl-g9)',
              }}
            >
              {c.label}
            </Box>
          );
        })}
      </Box>

      {/* ── featured ───────────────────────────────────────────────── */}
      {featured && (
        <Box
          component={Link}
          to={href(featured)}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            borderRadius: '32px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
            textDecoration: 'none',
          }}
        >
          <Box
            component="img"
            src={featuredImg}
            alt=""
            sx={{ flex: '1 1 420px', minWidth: 0, width: '100%', aspectRatio: '16 / 10', objectFit: 'cover', display: 'block' }}
          />
          <Box
            sx={{
              flex: '1 1 360px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 1.75,
              p: 'clamp(24px, 4vw, 44px)',
            }}
          >
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                p: '6px 10px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              Featured{labelFor[featured.category_slug] ? ` · ${labelFor[featured.category_slug]}` : ''}
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(26px, 3.6vw, 44px)',
                lineHeight: 1.05,
                letterSpacing: '-.02em',
                color: '#FFFFFF',
              }}
            >
              {featured.title}
            </Box>
            {featured.excerpt && (
              <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.6, color: '#C7DDD7' }}>
                {featured.excerpt}
              </Box>
            )}
            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-teal-l)' }}>
              {featured.read_time || 'Read'} →
            </Box>
          </Box>
        </Box>
      )}

      {/* ── the grid ───────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
          gap: 1.5,
        }}
      >
        {loading && [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Box key={i} sx={{ borderRadius: '28px', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
            <Box sx={{ aspectRatio: '16 / 10', bgcolor: 'var(--tl-mint-2)' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '18px 20px 22px' }}>
              <Box sx={{ height: 12, width: '44%', borderRadius: '6px', bgcolor: 'var(--tl-mint)' }} />
              <Box sx={{ height: 18, width: '82%', borderRadius: '6px', bgcolor: 'var(--tl-mint-2)' }} />
              <Box sx={{ height: 14, width: '96%', borderRadius: '6px', bgcolor: 'var(--tl-mint)' }} />
            </Box>
          </Box>
        ))}

        {!loading && rest.length === 0 && (
          <Box component="span" sx={{ fontFamily: UI, fontSize: 16, color: 'var(--tl-muted)', py: 2, px: 1 }}>
            Nothing published under this topic yet.
          </Box>
        )}

        {!loading && rest.map((a) => {
          const [bg, fg] = tileFor[a.category_slug] || TILE_PALETTE[3];
          const tag = labelFor[a.category_slug] || 'Health';
          return (
            <Box
              key={a.slug || a._id}
              component={Link}
              to={href(a)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '28px',
                overflow: 'hidden',
                bgcolor: '#FFFFFF',
                color: 'var(--tl-g9)',
                textDecoration: 'none',
                transition: 'box-shadow .2s var(--tl-ease)',
                '&:hover': { boxShadow: '0 12px 30px -16px rgba(11,43,39,0.35)' },
              }}
            >
              <Box
                component="span"
                sx={{
                  aspectRatio: '16 / 10',
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 2.5,
                  bgcolor: bg,
                  color: fg,
                  fontFamily: DISPLAY,
                  fontWeight: 700,
                  fontSize: 30,
                  lineHeight: 1.1,
                }}
              >
                {tag}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, p: '18px 20px 22px' }}>
                <Box
                  component="span"
                  sx={{
                    fontFamily: UI,
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    color: 'var(--tl-teal-d)',
                  }}
                >
                  {[tag, a.read_time].filter(Boolean).join(' · ')}
                </Box>
                <Box
                  component="span"
                  sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 21, lineHeight: 1.15, letterSpacing: '-.01em' }}
                >
                  {a.title}
                </Box>
                {a.excerpt && (
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted-2)' }}>
                    {a.excerpt}
                  </Box>
                )}
                <Box
                  component="span"
                  sx={{
                    mt: 'auto',
                    pt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontFamily: UI,
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--tl-teal-d)',
                  }}
                >
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: 'var(--tl-teal)',
                      color: '#FFFFFF',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 10,
                    }}
                  >
                    ✓
                  </Box>
                  Reviewed by a TrustLab pathologist
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Guides;
